import * as React from 'react'

import { Card } from '@/app/features/common/Card'
import { LoadingScreen } from '../../components/LoadingScreen'
import { DashboardIcon } from '@/app/features/icons/Dashboard'
import { FooterWithButtons } from '../../components/FooterWithButtons'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { axiosInstance } from '@/lib/axios'
import { SelectedDriver } from '@/app/types/creator'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  companyId: string
  selectedDrivers: SelectedDriver[]
  month: number // 1-12
  year: number
  onNext: () => void
  onPrev: () => void
}

type SummaryRow = {
  driverId: string
  name: string
  status: string
  totalGross: number
  lastCalculationDate: string
}

const getColumns = (
  tSafe: (key: string, defaultValue: string) => string,
): TableHeaderColumn[] => [
  {
    title: tSafe('creator.summary.column.driver', 'Kierowca'),
    key: 'name',
    type: 'string',
    sort: true,
  },
  {
    title: tSafe('creator.summary.column.status', 'Status'),
    key: 'status',
    type: 'string',
    sort: true,
  },
  {
    title: tSafe(
      'creator.summary.column.totalGross',
      'Koszty brutto łącznie',
    ),
    key: 'totalGross',
    type: 'number',
    sort: true,
  },
  {
    title: tSafe(
      'creator.summary.column.lastCalculationDate',
      'Data ostatniego przeliczenia',
    ),
    key: 'lastCalculationDate',
    type: 'date',
    sort: true,
  },
]

const buildDisplayName = (
  apiDriver: any,
  fallbackDriver: SelectedDriver | undefined,
  driverId: string,
): string => {
  const fromApi =
    apiDriver?.driver?.fullName ||
    [apiDriver?.driver?.name, apiDriver?.driver?.surname]
      .filter(Boolean)
      .join(' ')
      .trim()
  if (fromApi) return fromApi
  const fromSelected = [fallbackDriver?.name, fallbackDriver?.surname]
    .filter(Boolean)
    .join(' ')
    .trim()
  return fromSelected || `Kierowca ${driverId}`
}

const formatNumber = (value: number): string =>
  Number.isFinite(value)
    ? value.toLocaleString('pl-PL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '0,00'

const formatDate = (value: string): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('pl-PL')
}

export const SummarySecondSubstep = ({
  companyId,
  selectedDrivers,
  month,
  year,
  onNext,
  onPrev,
}: Props) => {
  const { tSafe } = useLocalize()
  const columns = React.useMemo(() => getColumns(tSafe), [tSafe])
  const [isLoading, setIsLoading] = React.useState(true)
  const [rows, setRows] = React.useState<SummaryRow[]>([])

  React.useEffect(() => {
    let cancelled = false
    const fetchSummary = async () => {
      const selectedDriverIds = selectedDrivers
        .map((d) => Number(d.driverId))
        .filter((id) => !Number.isNaN(id))

      if (!companyId || !selectedDriverIds.length) {
        if (!cancelled) {
          setRows([])
          setIsLoading(false)
        }
        return
      }

      try {
        const resp = await axiosInstance.post(
          `/api/${companyId}/timesheet/groupedTimesheet`,
          {
            companyId,
            drivers: selectedDriverIds,
            year,
            month,
            showBonusConfiguration: false,
          },
        )
        if (cancelled) return

        const apiRows: any[] = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
            ? resp.data
            : []

        const apiById = new Map<string, any>(
          apiRows
            .filter((row) => row?.driver?.id != null)
            .map((row) => [String(row.driver.id), row]),
        )
        const selectedById = new Map(
          selectedDrivers.map((d) => [String(d.driverId), d]),
        )

        const monthIndex = month - 1
        const built: SummaryRow[] = selectedDrivers.map((sd) => {
          const driverId = String(sd.driverId)
          const apiDriver = apiById.get(driverId)
          const hasContract = apiDriver?.hasContract?.[monthIndex] ?? 0
          const isCalculated = Boolean(apiDriver?.calculations?.[monthIndex])
          const status =
            hasContract === 0
              ? 'brak umowy'
              : isCalculated
                ? 'przeliczono'
                : 'w trakcie'

          return {
            driverId,
            name: buildDisplayName(apiDriver, selectedById.get(driverId), driverId),
            status,
            totalGross: Number(apiDriver?.summary ?? 0),
            lastCalculationDate: apiDriver?.lastCalculationDate ?? '',
          }
        })

        setRows(built)
      } catch (err) {
        console.error('Failed to fetch settlement summary', err)
        if (!cancelled) {
          setRows([])
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [companyId, selectedDrivers, year, month])

  const { sortedData, sortConfig, handleHeaderClick } =
    useSortableData<SummaryRow>({
      data: rows,
      tableColumns: columns,
      includeActiveSorting: false,
    })

  return (
    <Card
      className="tw-flex tw-min-h-0 tw-max-w-full tw-flex-1 tw-flex-col tw-bg-base-100 tw-pb-3"
      classNameContent="!tw-flex !tw-px-0 !tw-pt-0 tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden"
      paddingTop={false}
      noContentPadding
    >
      {isLoading ? (
        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center">
          <LoadingScreen
            text={tSafe(
              'creator.summary.loading',
              'Ładowanie podsumowania...',
            )}
          />
        </div>
      ) : (
        <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-t-xl">
          {sortedData.length > 0 ? (
            <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto">
              <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
                <TableHeader
                  opacity
                  sticky
                  rounded
                  disabledCheckboxToggle
                  columns={columns}
                  fontSize="sm"
                  sortConfig={sortConfig}
                  onHeaderClick={handleHeaderClick}
                />
                <tbody className="tw-text-sm tw-text-content-base">
                  {sortedData.map((row) => (
                    <tr
                      key={row.driverId}
                      className="notches-border--right tw-border-b tw-bg-white"
                    >
                      <TableTextRow size="medium" textSize="sm" pointer={false}>
                        {row.name}
                      </TableTextRow>
                      <TableTextRow size="medium" textSize="sm" pointer={false}>
                        <span
                          className={
                            row.status === 'przeliczono'
                              ? 'tw-rounded tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-text-emerald-700'
                              : row.status === 'brak umowy'
                                ? 'tw-rounded tw-bg-red-50 tw-px-2 tw-py-0.5 tw-text-red-700'
                                : 'tw-rounded tw-bg-amber-50 tw-px-2 tw-py-0.5 tw-text-amber-700'
                          }
                        >
                          {row.status}
                        </span>
                      </TableTextRow>
                      <TableTextRow size="medium" textSize="sm" pointer={false}>
                        {formatNumber(row.totalGross)}
                      </TableTextRow>
                      <TableTextRow size="medium" textSize="sm" pointer={false}>
                        {formatDate(row.lastCalculationDate)}
                      </TableTextRow>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <TableHeader
                opacity
                disabledCheckboxToggle
                columns={columns}
                sortConfig={null}
              />
              <TableEmpty
                title={tSafe(
                  'creator.summary.empty',
                  'Brak danych do wyświetlenia',
                )}
              />
            </>
          )}
        </section>
      )}

      <FooterWithButtons
        onNext={onNext}
        onPrev={onPrev}
        className="tw-shrink-0 tw-border-t tw-border-base-300"
        nextText={
          <span className="tw-flex tw-items-center tw-gap-2">
            <DashboardIcon />
            {tSafe(
              'creator.summary.backToDashboard',
              'Powrót na ekran główny',
            )}
          </span>
        }
      />
    </Card>
  )
}
